import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Skeleton,
  Stack,
} from '@hurdl/design-system';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState, type ReactElement } from 'react';

import {
  ItemEditorFrame,
  ReviewedEditor,
  sizeSuffix,
} from '../bespoke/menu-workspace/review-editor.js';
import {
  type Category,
  type ReviewedItem,
} from '../bespoke/menu-workspace/types.js';
import {
  ALLERGEN_BOOKLET_URL,
  restaurantLabelOf,
  useMenuOperations,
} from './menus-wiring.js';
import { useToast } from './toast-context.js';

/**
 * The full-page edit of one reviewed item (menus-spec R16).
 *
 * This is the edit flow's half of the one review-and-edit screen: it renders the reused
 * {@link ReviewedEditor} inside the same {@link ItemEditorFrame} the review flow uses, so
 * editing a published item looks and works exactly like reviewing a queued one — the
 * same sticky header, the same persistent "View PDF" control, the same section layout.
 * The difference the two flows keep is only what differs in the work: a reviewed item
 * carries editable allergen corrections rather than a categorise/confirm/reject decision.
 *
 * It was a modal `Dialog` opened from the Restaurant's grid before, which is why the
 * "View PDF" button — in the page header behind the modal — was covered while the form
 * was on screen. Making it a page removes the occlusion: the control lives in the form's
 * own sticky header and travels with it.
 *
 * Reads the Restaurant key and the item key from the route, loads the Restaurant's
 * reviewed items and managed Categories, and finds the one item. A key that is not a
 * reviewed item of this Restaurant shows a message and a link back to the menu rather
 * than a blank editor. A save is sequenced by the composed operations, reported in a
 * toast, and returns to the Restaurant's menu. The one `h1` is the frame's; the `main`
 * landmark is the Layout's.
 */
export function EditItemPage(): ReactElement {
  const { productKey, restaurantKey, itemKey } = useParams({ strict: false });
  const key = restaurantKey ?? '';
  const operations = useMenuOperations();
  const navigate = useNavigate();
  const { notify } = useToast();

  const [items, setItems] = useState<ReadonlyArray<ReviewedItem> | undefined>(
    undefined,
  );
  const [categories, setCategories] = useState<ReadonlyArray<Category>>([]);

  useEffect(() => {
    let live = true;
    void Promise.all([
      operations.listReviewed(key),
      operations.listCategories(key),
    ]).then(([nextItems, nextCategories]) => {
      if (!live) return;
      setItems(nextItems);
      setCategories(nextCategories);
    });
    return () => {
      live = false;
    };
  }, [operations, key]);

  const toMenu = (): void => {
    void navigate({
      to: '/products/$productKey/menus/$restaurantKey/items',
      params: { productKey: productKey ?? '', restaurantKey: key },
    });
  };

  if (items === undefined) {
    return (
      <Stack gap="large" aria-busy="true">
        <h1 className="font-hurdl-display text-hurdl-heading font-hurdl-semibold">
          Edit item
        </h1>
        <Skeleton className="h-96 w-full" />
      </Stack>
    );
  }

  const item = items.find((candidate) => candidate.itemKey === itemKey);

  if (item === undefined) {
    return (
      <Stack gap="large">
        <h1 className="font-hurdl-display text-hurdl-heading font-hurdl-semibold">
          Edit item
        </h1>
        <Alert tone="informational">
          <AlertTitle>Not on this menu</AlertTitle>
          <AlertDescription>
            This item is not a reviewed item of this restaurant.{' '}
            <Link
              to="/products/$productKey/menus/$restaurantKey/items"
              params={{ productKey: productKey ?? '', restaurantKey: key }}
              className="underline underline-offset-2"
            >
              Back to the menu
            </Link>
            .
          </AlertDescription>
        </Alert>
      </Stack>
    );
  }

  const title =
    item.displayName ?? item.extractedName + sizeSuffix(item.sizeVariant);
  const category = item.displayCategory ?? item.extractedCategory;

  const submit = (
    submission: Parameters<typeof operations.submitReviewedItem>[1],
  ): void => {
    void operations
      .submitReviewedItem(item.itemKey, submission)
      .then(() => {
        notify('success', `Saved ${title}`);
        toMenu();
      })
      .catch((error: unknown) => {
        notify('destructive', 'Edit not saved', messageOf(error));
      });
  };

  return (
    <ItemEditorFrame
      title={title}
      bookletUrl={ALLERGEN_BOOKLET_URL}
      badges={
        <>
          <Badge tone="neutral">{restaurantLabelOf(key)}</Badge>
          {category !== '' ? (
            <Badge tone="informational">{category}</Badge>
          ) : undefined}
        </>
      }
    >
      <ReviewedEditor
        key={item.itemKey}
        item={item}
        categories={categories}
        onSubmit={submit}
        onCancel={toMenu}
      />
    </ItemEditorFrame>
  );
}

/** The message of a thrown refusal, or a fallback where it carries none. */
function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Please try again.';
}
